import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ExternalLink, Coins, DollarSign, Bookmark, MessageCircle, Share2, AlertTriangle, Globe, ChevronDown, Check, Handshake } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import * as Recharts from "recharts";

const {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip: RechartsTooltip,
  ResponsiveContainer,
} = Recharts as any;
import ReportProblemDialog from "@/components/ReportProblemDialog";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";

interface HistoryAnuncio {
  id: string;
  item_nome: string;
  tipo: string;
  preco: number;
  moeda: string;
  preco_coins: number | null;
  preco_kk: number | null;
  servidor: string | null;
  status: string;
  tier: number | null;
  created_at: string;
  aprovado_em: string | null;
  expira_em: string | null;
  destaque: boolean;
  usuario: string;
  contato_telefone: string | null;
  contato_email?: string | null;
  descricao: string | null;
  anunciante_id?: string | null;
  anunciante_apelido?: string | null;
  anunciante_avatar_url?: string | null;
  preferencias_contato?: string[] | null;
}

interface ItemHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemNome: string;
  onOpenAnuncio?: (id: string) => void;
  sessionContato?: string | null;
  sessionToken?: string | null;
  onRequireLogin?: (action: () => void) => void;
  intermediatingAnuncios?: Set<string>;
}

const statusLabels: Record<string, string> = {
  aprovado: "Ativo",
  pendente: "Pendente",
  aguardando_pagamento: "Aguardando Pgto",
  rejeitado: "Rejeitado",
  expirado: "Expirado",
  pausado: "Pausado",
  vendido: "Vendido",
  encerrado: "Encerrado",
};

const statusColors: Record<string, string> = {
  aprovado: "bg-green-500/20 text-green-400",
  pendente: "bg-yellow-500/20 text-yellow-400",
  aguardando_pagamento: "bg-yellow-500/20 text-yellow-400",
  rejeitado: "bg-destructive/20 text-destructive",
  expirado: "bg-muted text-muted-foreground",
  pausado: "bg-blue-500/20 text-blue-400",
  vendido: "bg-primary/20 text-primary",
  encerrado: "bg-muted text-muted-foreground",
};

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const isActive = (a: HistoryAnuncio) => {
  if (a.status === "expirado" || a.status === "encerrado" || a.status === "vendido" || a.status === "rejeitado" || a.status === "pausado") return false;
  if (a.status !== "aprovado") return false;
  if (a.expira_em) {
    const expDate = new Date(a.expira_em);
    if (expDate.getTime() <= Date.now()) return false;
  }
  return true;
};

const fmtBrl = (v: number) => `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const fmtCoins = (v: number) => `${v.toLocaleString("pt-BR")} TC`;
const fmtKk = (v: number) => `${v.toLocaleString("pt-BR")} KK`;

const getFavorites = (): string[] => {
  try { return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]"); } catch { return []; }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) { favs.splice(idx, 1); localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs)); return false; }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

const ItemHistoryDialog = ({ open, onOpenChange, itemNome, onOpenAnuncio, sessionContato, sessionToken, onRequireLogin, intermediatingAnuncios }: ItemHistoryDialogProps) => {
  const [intermedioAnuncio, setIntermedioAnuncio] = useState<HistoryAnuncio | null>(null);
  const [loading, setLoading] = useState(false);
  const [allAnuncios, setAllAnuncios] = useState<HistoryAnuncio[]>([]);
  const [favs, setFavs] = useState<string[]>(getFavorites);
  const [filter, setFilter] = useState<"all" | "active" | null>(null);
  const [reportAnuncioId, setReportAnuncioId] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !itemNome) return;
    setLoading(true);
    setAllAnuncios([]);
    setFilter(null);
    setSelectedServer(null);
    supabase.functions.invoke("item-history", {
      body: { item_nome: itemNome, contato: sessionContato || undefined, token: sessionToken || undefined },
    }).then(({ data, error }) => {
      const fetched: HistoryAnuncio[] = data?.anuncios || [];
      setAllAnuncios(fetched);
      // Auto-select first server
      const servers = [...new Set(fetched.map(a => a.servidor).filter(Boolean))] as string[];
      if (servers.length > 0) setSelectedServer(servers[0]);
      setLoading(false);
    });
  }, [open, itemNome]);

  const availableServers = useMemo(() => {
    return ([...new Set(allAnuncios.map(a => a.servidor).filter(Boolean))] as string[]).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [allAnuncios]);

  const anuncios = useMemo(() => {
    if (!selectedServer) return allAnuncios;
    return allAnuncios.filter(a => a.servidor === selectedServer);
  }, [allAnuncios, selectedServer]);

  const activeOnes = useMemo(() => anuncios.filter(isActive), [anuncios]);
  // Anúncios "publicados" para fins de estatística: exclui rascunhos/pendentes que ainda não viraram anúncio público
  const publishedOnes = useMemo(
    () => anuncios.filter(a => !["pendente", "aguardando_pagamento", "rejeitado"].includes(a.status)),
    [anuncios]
  );
  const filteredAnuncios = useMemo(() => {
    if (filter === "active") return activeOnes;
    return anuncios;
  }, [anuncios, activeOnes, filter]);

  const showActiveStats = filter === "active";

  const summary = useMemo(() => {
    if (anuncios.length === 0) return null;
    const source = showActiveStats ? activeOnes : publishedOnes;
    const precosBrl = source.filter(a => a.moeda === "brl" && a.preco > 0).map(a => a.preco);
    const precosCoins = source.filter(a => a.moeda === "coins" && a.preco > 0).map(a => a.preco);

    const stats = (arr: number[]) => arr.length === 0 ? null : ({
      max: Math.max(...arr),
      min: Math.min(...arr),
      avg: arr.reduce((a, b) => a + b, 0) / arr.length,
    });

    const sorted = [...source].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    return {
      totalAnuncios: publishedOnes.length,
      ativosCount: activeOnes.length,
      brl: stats(precosBrl),
      coins: stats(precosCoins),
      chartData: sorted
        .filter(a => a.moeda === "brl" && a.preco > 0)
        .map(a => ({
          date: new Date(a.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
          preco: a.preco,
        })),
    };
  }, [anuncios, activeOnes, showActiveStats]);

  const handleFavorite = (id: string) => {
    const added = toggleFavorite(id);
    setFavs(getFavorites());
    toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
  };

  const handleWhatsApp = (a: HistoryAnuncio) => {
    if (!a.contato_telefone) {
      toast({ title: "Contato não disponível", variant: "destructive" });
      return;
    }
    const msg = encodeURIComponent(`Olá! Tenho interesse no item "${a.item_nome}" anunciado no RPG Coin.`);
    window.open(`https://wa.me/${a.contato_telefone}?text=${msg}`, "_blank");
  };

  const handleShare = (id: string) => {
    const url = `${window.location.origin}/item/${id}`;
    navigator.clipboard.writeText(url).then(() => toast({ title: "Link copiado!" }));
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary gold-text-glow flex items-center gap-2">
            <img
              src={getItemImageUrl(itemNome)}
              alt={itemNome}
              className="w-7 h-7 object-contain image-rendering-pixelated"
              onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
            />
            {itemNome}
            <span className="text-[10px] text-muted-foreground font-normal">({publishedOnes.length} {publishedOnes.length === 1 ? "anúncio" : "anúncios"})</span>
          </DialogTitle>
        </DialogHeader>

        {!loading && availableServers.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Globe className="w-3 h-3 text-muted-foreground" />
            <span className="font-pixel text-[7px] text-muted-foreground uppercase">Servidor:</span>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setSelectedServer(null)}
                className={`font-pixel text-[7px] px-2 py-1 rounded-sm transition-all cursor-pointer ${
                  !selectedServer ? "bg-primary/20 text-primary ring-1 ring-primary/50" : "bg-secondary/30 text-muted-foreground hover:text-foreground"
                }`}
              >
                Todos
              </button>
              {availableServers.map(srv => (
                <button
                  key={srv}
                  onClick={() => setSelectedServer(srv)}
                  className={`font-pixel text-[7px] px-2 py-1 rounded-sm transition-all cursor-pointer ${
                    selectedServer === srv ? "bg-primary/20 text-primary ring-1 ring-primary/50" : "bg-secondary/30 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {srv}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[70vh] space-y-4 pr-1">
            {/* Summary Cards */}
            {summary && (
              <div className="flex flex-wrap gap-2">
                <ToggleCard
                  label="Total"
                  value={summary.totalAnuncios}
                  active={filter !== "active"}
                  onClick={() => setFilter(null)}
                />
                <ToggleCard
                  label="Ativos"
                  value={summary.ativosCount}
                  active={filter === "active"}
                  highlight
                  onClick={() => setFilter(f => f === "active" ? null : "active")}
                />
                {summary.brl && <StatCard label={`Min ${showActiveStats ? "Ativo" : ""} (R$)`} value={fmtBrl(summary.brl.min)} />}
                {summary.brl && <StatCard label={`Max ${showActiveStats ? "Ativo" : ""} (R$)`} value={fmtBrl(summary.brl.max)} />}
                {summary.coins && <StatCard label={`Min ${showActiveStats ? "Ativo" : ""} (TC)`} value={fmtCoins(summary.coins.min)} />}
                {summary.coins && <StatCard label={`Max ${showActiveStats ? "Ativo" : ""} (TC)`} value={fmtCoins(summary.coins.max)} />}
                {summary.brl && <StatCard label="Média (R$)" value={fmtBrl(summary.brl.avg)} />}
                {summary.coins && <StatCard label="Média (TC)" value={fmtCoins(summary.coins.avg)} />}
              </div>
            )}

            {/* Anuncios List */}
            <div className="space-y-2">
              {filteredAnuncios.length === 0 ? (
                <p className="text-center text-xs text-muted-foreground py-8">Nenhum anúncio encontrado</p>
              ) : (
                filteredAnuncios.map((a) => {
                  const active = isActive(a);
                  const isExpiredAprovado = a.status === "aprovado" && a.expira_em && new Date(a.expira_em).getTime() <= Date.now();
                  const isFav = favs.includes(a.id);
                  return (
                    <div key={a.id} className="tibia-border-inner bg-secondary/30 rounded-sm p-3 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(a.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className={`font-pixel text-[7px] px-1.5 py-0.5 rounded-sm ${a.tipo === "venda" ? "bg-primary/20 text-primary" : "bg-accent/20 text-accent"}`}>
                          {a.tipo === "venda" ? "VENDA" : "COMPRA"}
                        </span>
                        {active ? (
                          <span className="font-pixel text-[7px] px-1.5 py-0.5 rounded-sm bg-green-500/20 text-green-400">🟢 Ativo</span>
                        ) : (
                          <span className={`font-pixel text-[7px] px-1.5 py-0.5 rounded-sm ${isExpiredAprovado ? "bg-muted text-muted-foreground" : (statusColors[a.status] || "bg-muted text-muted-foreground")}`}>
                            {isExpiredAprovado ? "Expirado" : (statusLabels[a.status] || a.status)}
                          </span>
                        )}
                        {a.tier != null && a.tier > 0 && (
                          <span className="w-5 h-5 rounded-full bg-[#FF8C00] flex items-center justify-center border border-[#FF6B00] text-white font-pixel text-[8px] font-bold">
                            {a.tier}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] text-foreground/80">👤 {a.usuario}</span>
                        {a.servidor && (
                          <span className="text-[10px] text-muted-foreground">🌐 {a.servidor}</span>
                        )}
                        <div className="flex items-center gap-1">
                          {(a.moeda === "brl" || a.preco > 0) && a.moeda !== "coins" && (
                            <span className="flex items-center gap-0.5 text-[10px] text-primary font-bold">
                              <DollarSign className="w-3 h-3" />
                              {fmtBrl(a.preco)}
                            </span>
                          )}
                          {a.moeda === "coins" && (
                            <span className="flex items-center gap-0.5 text-[10px] text-primary font-bold">
                              <Coins className="w-3 h-3" />
                              {fmtCoins(a.preco)}
                            </span>
                          )}
                          {a.preco_coins && a.preco_coins > 0 && a.moeda !== "coins" && (
                            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              / <Coins className="w-3 h-3" /> {fmtCoins(a.preco_coins)}
                            </span>
                          )}
                          {a.preco_kk != null && a.preco_kk > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-amber-400 font-bold">
                              / {fmtKk(a.preco_kk)}
                            </span>
                          )}
                        </div>
                      </div>
                      {a.descricao && (
                        <p className="text-[9px] text-muted-foreground italic leading-relaxed pt-0.5 break-all whitespace-pre-wrap">
                          📝 {a.descricao}
                        </p>
                      )}
                      {/* Action buttons */}
                      {active && (
                        <div className="flex items-center gap-1 pt-0.5 border-t border-border/30">
                          {/* Contatar (ContactOptionsPopover quando logado, fallback WhatsApp simples) */}
                          {sessionContato && sessionToken ? (
                            <ContactOptionsPopover
                              anuncioId={a.id}
                              itemNome={a.item_nome}
                              temWhatsapp={!!a.contato_telefone}
                              temEmail={!!a.contato_email}
                              anuncianteId={a.anunciante_id || null}
                              anuncianteApelido={a.anunciante_apelido || null}
                              anuncianteAvatarUrl={a.anunciante_avatar_url || null}
                              preferenciasContato={a.preferencias_contato || ["whatsapp"]}
                              onRequireLogin={onRequireLogin}
                              sessionContato={sessionContato}
                              sessionToken={sessionToken}
                            >
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <button className="p-1 hover:scale-110 transition-all">
                                    <MessageCircle className="w-3.5 h-3.5 text-green-500/70 hover:text-green-400" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="z-[100]">Contatar anunciante</TooltipContent>
                              </Tooltip>
                            </ContactOptionsPopover>
                          ) : (
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <button onClick={() => handleWhatsApp(a)} className="p-1 hover:scale-110 transition-all">
                                  <MessageCircle className="w-3.5 h-3.5 text-green-500/70 hover:text-green-400" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="z-[100]">Contatar anunciante</TooltipContent>
                            </Tooltip>
                          )}

                          {/* Intermédio */}
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => {
                                  const doOpen = () => setIntermedioAnuncio(a);
                                  if (!sessionContato || !sessionToken) {
                                    if (onRequireLogin) onRequireLogin(doOpen);
                                    else toast({ title: "Faça login para solicitar intermédio", variant: "destructive" });
                                    return;
                                  }
                                  doOpen();
                                }}
                                disabled={intermediatingAnuncios?.has(a.id)}
                                className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                              >
                                <Handshake className="w-3.5 h-3.5 text-purple-400/70 hover:text-purple-400" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[100]">{intermediatingAnuncios?.has(a.id) ? "Em intermédio" : "Solicitar intermédio"}</TooltipContent>
                          </Tooltip>

                          {/* Lista (favorito) */}
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleFavorite(a.id)} className="p-1 hover:scale-110 transition-all">
                                <Bookmark className={`w-3.5 h-3.5 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[100]">Adicionar à lista</TooltipContent>
                          </Tooltip>

                          {/* Compartilhar */}
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleShare(a.id)} className="p-1 hover:scale-110 transition-all">
                                <Share2 className="w-3.5 h-3.5 text-blue-400/70 hover:text-blue-400" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[100]">Compartilhar</TooltipContent>
                          </Tooltip>

                          {/* Reportar */}
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button onClick={() => setReportAnuncioId(a.id)} className="p-1 hover:scale-110 transition-all">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-400/70 hover:text-red-400" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[100]">Reportar problema</TooltipContent>
                          </Tooltip>

                          <div className="flex-1" />
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => { onOpenAnuncio?.(a.id); onOpenChange(false); }}
                                className="tibia-tab rounded-sm px-2 py-1 hover:text-primary"
                                title="Ver anúncio"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[100]">Ver anúncio</TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Chart after list */}
            {summary && summary.chartData.length > 1 && (
              <div className="tibia-border-inner bg-secondary/30 rounded-sm p-3">
                <span className="font-pixel text-[7px] text-primary uppercase tracking-wider">Evolução de Preço (R$)</span>
                <div className="h-[100px] mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary.chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 8, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 8, fill: "hsl(var(--muted-foreground))" }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 4,
                          fontSize: 11,
                        }}
                        formatter={(value: number) => fmtBrl(value)}
                      />
                      <Line type="monotone" dataKey="preco" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Report Problem Dialog */}
        {reportAnuncioId && (
          <ReportProblemDialog
            open={!!reportAnuncioId}
            onOpenChange={(o) => { if (!o) setReportAnuncioId(null); }}
            anuncioId={reportAnuncioId}
            itemNome={itemNome}
          />
        )}

        {/* Solicitar Intermédio Dialog */}
        {intermedioAnuncio && sessionContato && sessionToken && (
          <SolicitarIntermedioDialog
            open={!!intermedioAnuncio}
            onOpenChange={(o) => { if (!o) setIntermedioAnuncio(null); }}
            anuncioId={intermedioAnuncio.id}
            itemNome={intermedioAnuncio.item_nome}
            preco={intermedioAnuncio.preco}
            moeda={intermedioAnuncio.moeda}
            precoCoins={intermedioAnuncio.preco_coins}
            precoKk={intermedioAnuncio.preco_kk}
            servidor={intermedioAnuncio.servidor}
            descricao={intermedioAnuncio.descricao}
            anuncianteApelido={intermedioAnuncio.anunciante_apelido || null}
            anuncianteAvatarUrl={intermedioAnuncio.anunciante_avatar_url || null}
            sessionContato={sessionContato}
            sessionToken={sessionToken}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) => (
  <div className="tibia-border-inner bg-secondary/30 rounded-sm p-2 text-center">
    <div className="font-pixel text-[7px] text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className={`text-sm font-bold ${highlight ? "text-green-400" : "text-primary"}`}>{value}</div>
  </div>
);

const ToggleCard = ({ label, value, active, highlight, onClick }: { label: string; value: string | number; active: boolean; highlight?: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`tibia-border-inner rounded-sm p-2 text-center transition-all cursor-pointer ${
      active ? "bg-primary/20 ring-1 ring-primary/50" : "bg-secondary/30 opacity-70 hover:opacity-100"
    }`}
  >
    <div className="font-pixel text-[7px] text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className={`text-sm font-bold ${highlight ? "text-green-400" : "text-primary"}`}>{value}</div>
  </button>
);

export default ItemHistoryDialog;
